/**
 * CopyOrgNameButton: whose books these are, on the clipboard in one tap.
 *
 * The header answers "where am I" with the business name, but at h-10 it has
 * 240px to say it in, so "BUNGAS SUPERSTRUCTURES PRIVATE LIMITED" arrives as
 * "BUNGAS SUPERSTRUCTU…". That name is the thing you retype all day: into a
 * WhatsApp reply, a filing portal, a covering letter, a folder. Retyping a name
 * you can only half read is how a client's legal name ends up spelled two ways
 * across their own paperwork.
 *
 * So the tick is the outcome and the toast is the proof: it shows the FULL name
 * that went to the clipboard, which is the half the header could not print.
 *
 * It does NOT sit in the header row. It used to, and there it spent horizontal
 * width on a control almost nobody presses, in the one row where width is the
 * scarce thing and the org name is already losing its tail to an ellipsis. It
 * now lives at the top of the org dropdown and in the mobile drawer's own row:
 * both are places the name is shown whole, which is what a copy is for.
 *
 * `stopPropagation` is not decoration wherever this sits next to a switcher:
 * without it a copy would also toggle the menu it is sitting in.
 *
 * @since orgcopy (2026-08-27), moved out of the header copymenu (2026-08-28)
 */
import { useState, type MouseEvent } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from '@/shared/ui/toast';

interface CopyOrgNameButtonProps {
    /** The name to copy. Renders nothing without one, rather than an inert button. */
    name?: string | null;
    /** Icon size in px. 13 in the org dropdown, 14 in the mobile drawer. */
    size?: number;
    /**
     * Padding class for the hit area. Its own prop rather than something to
     * pass through `className`, because two Tailwind padding classes on one
     * element are decided by stylesheet order, not by which one you typed last:
     * an overriding `p-0.5` appended after `p-1` silently loses.
     */
    pad?: string;
    className?: string;
}

export function CopyOrgNameButton({ name, size = 13, pad = 'p-1', className = '' }: CopyOrgNameButtonProps) {
    const [copied, setCopied] = useState(false);
    const trimmed = name?.trim();
    if (!trimmed) return null;

    const copy = async (e: MouseEvent<HTMLButtonElement>) => {
        // The switcher button is our sibling and the dropdown is our parent's
        // child: a bare click here would copy the name AND open the menu.
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(trimmed);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
            toast.success('Name copied', { description: trimmed });
        } catch {
            // No Settings tab fixes a blocked clipboard, so there is no button to
            // send them to. Put the name itself in the toast instead: it is
            // selectable there, which is the only thing they actually wanted.
            toast.error('Could not copy the name', {
                description: `This browser blocked the clipboard. The full name is: ${trimmed}`,
            });
        }
    };

    return (
        <button
            type="button"
            onClick={copy}
            aria-label={copied ? 'Name copied' : `Copy the name ${trimmed}`}
            title={`Copy "${trimmed}"`}
            className={`shrink-0 ${pad} rounded transition-colors text-stone-400 hover:text-teal-700 hover:bg-teal-50 dark:text-stone-500 dark:hover:text-teal-400 dark:hover:bg-teal-950/40 ${className}`}
        >
            {copied
                ? <Check size={size} className="text-teal-600 dark:text-teal-400" />
                : <Copy size={size} />}
        </button>
    );
}
